import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getOpsClasses } from '../../../requests';
import { getOpsClassExceptionsEx } from '../../../requests/opsRegistrationsExtra';
import {
  btnPrimary,
  Card,
  EmptyState,
  inputClass,
  OriginBadge,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
  SortTh,
  useListSort,
} from '../ui';

const STATUSES = ['Available', 'Cancelled', 'Rescheduled', 'Completed'];
const TYPES = ['Public Class', 'Private Class'];

const OpsClasses: React.FC = () => {
  const navigate = useNavigate();
  // Links from other screens land here with a filter in the URL: an
  // account's classes, a program's classes, an instructor's classes.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get('accountId') ?? '';
  const urlProgramId = searchParams.get('programId') ?? '';
  const urlInstructor = searchParams.get('instructor') ?? '';
  const urlLabel = searchParams.get('label') ?? '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [classType, setClassType] = useState('');
  const [unassigned, setUnassigned] = useState(false);
  const [when, setWhen] = useState<'upcoming' | 'past' | 'all'>(
    (searchParams.get('when') as 'upcoming' | 'past' | 'all') || 'upcoming',
  );
  const [prepCall, setPrepCall] = useState('');
  const [page, setPage] = useState(1);
  const { sortKey, sortDir, toggleSort } = useListSort();

  // The scheduler's exception queue: upcoming classes with nobody teaching
  const { data: exceptions } = useQuery({
    queryKey: ['opsClassExceptions'],
    queryFn: () => getOpsClassExceptionsEx(90),
  });

  // Debounce typing into the search box
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'opsClasses',
      q,
      status,
      classType,
      unassigned,
      when,
      prepCall,
      sortKey,
      sortDir,
      page,
    ],
    queryFn: () =>
      getOpsClasses({
        sort: sortKey || undefined,
        dir: sortKey ? sortDir : undefined,
        q: q || undefined,
        status: status || undefined,
        classType: classType || undefined,
        unassigned: unassigned || undefined,
        when,
        prepCall: prepCall || undefined,
        accountId: urlAccountId || undefined,
        programId: urlProgramId || undefined,
        instructor: urlInstructor || undefined,
        page,
      }),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Classes"
        meta={data && `${data.total.toLocaleString()} classes`}
        actions={
          <button
            className={btnPrimary}
            onClick={() => navigate(PATH.opsClassNew)}
          >
            New class
          </button>
        }
      />
      <Toolbar>
        <input
          className={`${inputClass} w-64`}
          placeholder="Search name or class number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={inputClass}
          value={when}
          onChange={(e) => {
            setWhen(e.target.value as 'upcoming' | 'past' | 'all');
            setPage(1);
          }}
        >
          <option value="upcoming">Upcoming · soonest first</option>
          <option value="past">Past · most recent first</option>
          <option value="all">All · newest first</option>
        </select>
        <select
          className={inputClass}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select
          className={inputClass}
          value={classType}
          onChange={(e) => {
            setClassType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Public + private</option>
          {TYPES.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-four">
          <input
            type="checkbox"
            className="ops-check"
            checked={unassigned}
            onChange={(e) => {
              setUnassigned(e.target.checked);
              setPage(1);
            }}
          />
          No instructor yet
        </label>
        {(urlAccountId || urlProgramId || urlInstructor) && (
          <button
            className="rounded-full border border-blue-200 bg-blue-100/60 px-2.5 py-1 text-xs text-blue-primary"
            onClick={() => setSearchParams({})}
            title="Show every class"
          >
            {urlLabel ||
              (urlAccountId
                ? 'one account'
                : urlProgramId
                ? 'one program'
                : 'one instructor')}{' '}
            only ×
          </button>
        )}
        {prepCall && (
          <button
            className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs text-amber-900"
            onClick={() => {
              setPrepCall('');
              setPage(1);
            }}
            title="Clear"
          >
            prep call still needed ×
          </button>
        )}
      </Toolbar>
      {exceptions && exceptions.noInstructor.count > 0 && (
        <div className="mx-6 mb-4 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">
            {exceptions.noInstructor.count} upcoming{' '}
            {exceptions.noInstructor.count === 1 ? 'class has' : 'classes have'}{' '}
            no instructor
          </span>
          <span className="text-amber-800">
            ({exceptions.noInstructor.private} private ·{' '}
            {exceptions.noInstructor.public} public, next {exceptions.days} days
            · soonest{' '}
            {exceptions.noInstructor.items[0]?.daysOut === 0
              ? 'today'
              : `in ${exceptions.noInstructor.items[0]?.daysOut} days`}
            )
          </span>
          <button
            className="ml-auto underline"
            onClick={() => {
              setUnassigned(true);
              setStatus('');
              setWhen('upcoming');
              setPrepCall('');
              setPage(1);
            }}
          >
            Work the queue
          </button>
        </div>
      )}
      {/* The other two queues that must end empty: prep calls in the next
          14 days, travel for onsites in the next 45 (same banner style) */}
      {exceptions && exceptions.prepCallsDue?.count > 0 && (
        <div className="mx-6 mb-4 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">
            {exceptions.prepCallsDue.count} private{' '}
            {exceptions.prepCallsDue.count === 1 ? 'class' : 'classes'} in the
            next {exceptions.prepCallsDue.days} days still{' '}
            {exceptions.prepCallsDue.count === 1 ? 'needs' : 'need'} a prep call
          </span>
          <span className="text-amber-800">
            (soonest{' '}
            {exceptions.prepCallsDue.items[0]?.daysOut === 0
              ? 'today'
              : `in ${exceptions.prepCallsDue.items[0]?.daysOut} days`}
            {exceptions.prepCallsDue.items[0]?.accountName
              ? ` · ${exceptions.prepCallsDue.items[0].accountName}`
              : ''}
            )
          </span>
          <button
            className="ml-auto underline"
            onClick={() => {
              setPrepCall('due');
              setClassType('Private Class');
              setUnassigned(false);
              setStatus('');
              setWhen('upcoming');
              setPage(1);
            }}
          >
            List them
          </button>
        </div>
      )}
      {exceptions && exceptions.travelDue?.count > 0 && (
        <div className="mx-6 mb-4 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">
            {exceptions.travelDue.count} onsite{' '}
            {exceptions.travelDue.count === 1 ? 'class' : 'classes'} in the next{' '}
            {exceptions.travelDue.days} days with travel not yet booked
          </span>
          <span className="text-amber-800">
            (soonest{' '}
            {exceptions.travelDue.items[0]?.daysOut === 0
              ? 'today'
              : `in ${exceptions.travelDue.items[0]?.daysOut} days`}
            {exceptions.travelDue.items[0]?.instructorName
              ? ` · ${exceptions.travelDue.items[0].instructorName}`
              : ''}
            )
          </span>
          {exceptions.travelDue.items[0] && (
            <button
              className="ml-auto underline"
              onClick={() =>
                navigate(
                  PATH.opsClassEdit.replace(
                    ':id',
                    exceptions.travelDue.items[0].id,
                  ),
                )
              }
            >
              Open the soonest
            </button>
          )}
        </div>
      )}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            {data && data.items.length === 0 ? (
              <EmptyState
                title="No classes match"
                hint="Try clearing the search or widening the status filter."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className={`${tableClass} min-w-[900px]`}>
                  <thead className={theadClass}>
                    <tr>
                      <SortTh
                        k="start"
                        label="Start"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="name"
                        label="Class"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="type"
                        label="Type"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="status"
                        label="Status"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="instructor"
                        label="Instructor"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="account"
                        label="Account"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="seats"
                        label="Seats"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                        right
                      />
                      <SortTh
                        k="number"
                        label="#"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((c) => (
                      <tr
                        key={c.id}
                        className={rowClickClass}
                        onClick={() =>
                          navigate(PATH.opsClassEdit.replace(':id', c.id))
                        }
                      >
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {c.start ? c.start.slice(0, 10) : '—'}
                        </td>
                        <td className={`${tdClass} font-medium text-gray-four`}>
                          {c.name}
                          {c.series?.count != null && (
                            <span
                              className="ml-2 rounded bg-grey-100 px-1.5 py-0.5 text-[11px] font-medium text-grey-700"
                              title="Session i of N in a series"
                            >
                              {c.series.index}/{c.series.count}
                            </span>
                          )}
                          {c.localOrigin && (
                            <span className="ml-2">
                              <OriginBadge local />
                            </span>
                          )}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {c.classType ?? '—'}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          <Pill value={c.status} />
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {c.instructorName ?? '—'}
                        </td>
                        <td className={tdClass}>{c.accountName ?? '—'}</td>
                        <td className={`${tdClass} text-right`}>
                          {c.seats ?? '—'}
                        </td>
                        <td
                          className={`${tdClass} whitespace-nowrap text-gray-bold`}
                        >
                          {c.classNumber ?? ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        </PageBody>
      )}
    </>
  );
};

export default OpsClasses;
